import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Check, Briefcase, User } from 'lucide-react';

const TXT = {
  en: {
    analyzeTitle: "Let's Analyze Your Website",
    analyzeSub: 'Enter your website and tell us what type it is',
    urlLabel: 'Website URL *',
    urlHelp: "We'll analyze this URL for performance, SEO, and opportunities",
    typeLabel: 'Website Type *',
    business: 'Business',
    businessDesc: 'For companies & commercial sites',
    personal: 'Personal',
    personalDesc: 'For portfolios & blogs',
    back: 'Back',
    next: 'Next',
    sitesAnalyzed: '500+ Websites Analyzed',
    starRated: '5-Star Rated',
    securePrivate: 'Secure & Private',
    reviewUrl: 'Website URL',
    // Step 2
    goalsTitle: 'Your Goals & Challenges',
    goalsSub: "Help us understand what you're trying to achieve",
    objectivesLabel: 'What are your main objectives? (Select at least 1) *',
    challengesLabel: 'Current Challenges',
    challengesOpt: '(Optional - Leave blank if unsure)',
    // Step 3
    detailsTitle: 'Additional Details',
    detailsSub: "Optional - Leave blank if you don't know",
    bizTypeLabel: 'Specific Business Type',
    bizTypeOpt: '(Optional)',
    selectType: 'Select a type...',
    specifyType: 'Please specify your business type',
    industryLabel: 'Industry or Niche',
    industryHint: '(e.g., Healthcare, E-commerce)',
    industryPh: 'Leave blank if unsure',
    mobileAppQ: 'Do you currently have a mobile app for your business?',
    mobileAppYes: 'Yes', mobileAppNo: 'No', mobileAppPlan: 'Planning To',
    mobileAppLink: 'App Store or Google Play link',
    ga4Q: 'Do you have Google Analytics 4 (GA4) installed?',
    ga4Yes: 'Yes', ga4No: 'No', ga4Unknown: "Don't Know",
    organicSessions: 'Monthly organic sessions',
    leaveBlank: '(Leave blank if unknown)',
  },
  es: {
    analyzeTitle: 'Analicemos tu Sitio Web',
    analyzeSub: 'Ingresa tu sitio web y dinos de qué tipo es',
    urlLabel: 'URL del Sitio Web *',
    urlHelp: 'Analizaremos esta URL para rendimiento, SEO y oportunidades',
    typeLabel: 'Tipo de Sitio Web *',
    business: 'Negocio',
    businessDesc: 'Para empresas y sitios comerciales',
    personal: 'Personal',
    personalDesc: 'Para portafolios y blogs',
    back: 'Atrás',
    next: 'Siguiente',
    sitesAnalyzed: '500+ Sitios Analizados',
    starRated: 'Calificación 5 Estrellas',
    securePrivate: 'Seguro y Privado',
    reviewUrl: 'URL del Sitio Web',
    // Step 2
    goalsTitle: 'Tus Objetivos y Desafíos',
    goalsSub: 'Ayúdanos a entender qué estás intentando lograr',
    objectivesLabel: '¿Cuáles son tus objetivos principales? (Selecciona al menos 1) *',
    challengesLabel: 'Desafíos Actuales',
    challengesOpt: '(Opcional - Déjalo en blanco si no estás seguro)',
    // Step 3
    detailsTitle: 'Detalles Adicionales',
    detailsSub: 'Opcional - Déjalo en blanco si no lo sabes',
    bizTypeLabel: 'Tipo Específico de Negocio',
    bizTypeOpt: '(Opcional)',
    selectType: 'Selecciona un tipo...',
    specifyType: 'Por favor especifica tu tipo de negocio',
    industryLabel: 'Industria o Nicho',
    industryHint: '(ej., Salud, E-commerce)',
    industryPh: 'Déjalo en blanco si no estás seguro',
    mobileAppQ: '¿Tienes actualmente una app móvil para tu negocio?',
    mobileAppYes: 'Sí', mobileAppNo: 'No', mobileAppPlan: 'Planeando',
    mobileAppLink: 'Enlace de App Store o Google Play',
    ga4Q: '¿Tienes instalado Google Analytics 4 (GA4)?',
    ga4Yes: 'Sí', ga4No: 'No', ga4Unknown: 'No Lo Sé',
    organicSessions: 'Sesiones orgánicas mensuales',
    leaveBlank: '(Déjalo en blanco si no lo sabes)',
  },
};

const AnalyzerWizard = ({ onComplete }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0] === 'es' ? 'es' : 'en';
  const tx = TXT[lang];
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: URL
    url: '',
    
    // Step 2: Type
    websiteType: '', // 'business' or 'personal'
    
    // Step 3: Objectives
    objectives: [],
    
    // Step 4: Industry/Niche
    industry: '',
    
    // Step 5: Current Challenges
    challenges: [],
    
    // Mobile App
    hasMobileApp: '',
    mobileAppUrl: '',
    
    // Step 6: GA4 & Analytics
    hasGA4: '',
    ga4LinkedToGSC: '',
    tracksConversions: '',
    
    // Step 7: Performance Metrics (conditional on GA4)
    monthlyOrganicSessions: '',
    bounceRate: '',
    avgEngagementTime: '',
    hasEnhancedMeasurement: '',
    
    // Step 8+: Business/Personal specific
    businessModel: '',
    targetAudience: '',
    monthlyTrafficGoal: '',
    monthlyRevenueGoal: '',
    keyCompetitors: '',
    currentTools: '',
    marketingChannels: [],
    budgetRange: '',
    timeline: '',
    performanceBaseline: '',
    
    // Car/RV Dealer specific
    inventoryFeeds: [],
    inventoryFeedsOther: '',
    hasServiceScheduling: '',
    serviceDeptFeatures: [],
    inventorySize: '',
    numberOfLocations: '',
    dealershipType: '',
    
    // Personal specific
    personalPurpose: '',
    targetAudienceSize: '',
    contentUpdateFrequency: '',
    
    // Voice Search & AI Visibility
    voiceSearchAware: '', // yes, no, not_sure
    appearsInAiSearch: '', // yes, no, not_sure
    aiPlatformsMentioned: [], // ChatGPT, Gemini, Perplexity, Claude, Copilot
    voiceSearchOptimized: '', // yes, no, partially
    structuredDataImplemented: '', // yes, no, not_sure
    
    // Additional context
    siteType: '', // eCommerce, Blog, SaaS, etc.
    siteTypeOther: '', // Custom input when "Other" is selected
    businessModelOther: '', // For business model "Other"
    personalPurposeOther: '', // For personal purpose "Other"
    goals: []
  });

  const totalSteps = 4; // Simplified to 4 steps for better UX

  const objectiveOptions = [
    'Generate leads (contact forms, sign-ups)',
    'Sell products or services',
    'Build brand awareness',
    'Share content or information',
    'Drive traffic and improve SEO rankings',
    'Increase user engagement',
    'Enhance user experience',
    'Collect user data or feedback',
    'Monetize through ads or affiliates',
    'Support community building'
  ];

  const challengeOptions = [
    'Slow loading times',
    'Poor mobile experience',
    'Low search rankings',
    'High bounce rate',
    'Low conversion rate',
    'Outdated design',
    'Security concerns',
    'Poor navigation/UX',
    'Limited traffic',
    'Technical errors'
  ];

  const marketingChannelOptions = [
    'Organic search',
    'Social media',
    'Paid ads',
    'Email marketing',
    'Referrals',
    'Direct traffic',
    'Content marketing',
    'Influencer marketing'
  ];

  const siteTypeOptions = [
    'eCommerce Store',
    'Restaurant / Food Service',
    'Dental Practice',
    'Medical Practice / Healthcare',
    'Car Dealership',
    'RV Dealership',
    'Auto Repair / Service',
    'Cleaning Service',
    'Law Firm',
    'Real Estate',
    'Contractor / Home Services',
    'Salon / Spa',
    'Gym / Fitness',
    'Blog/Content Site',
    'SaaS Platform',
    'Non-Profit',
    'Other'
  ];

  const handleMultiSelect = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(item => item !== value)
        : [...prev[field], value]
    }));
  };

  // Check if URL is blocked (our own site)
  const isBlockedUrl = (url) => {
    const urlLower = (url || '').toLowerCase();
    return urlLower.includes('web-techservices.com') || urlLower.includes('webtechservices.com');
  };

  // Handle website type selection with validation
  const handleWebsiteTypeSelect = (type) => {
    if (!formData.url) {
      alert('Please enter a website URL first');
      return;
    }
    if (isBlockedUrl(formData.url)) {
      alert("😊 Thanks, but we already know our website is awesome! Please enter a different URL to analyze.");
      return;
    }
    setFormData({...formData, websiteType: type});
    setTimeout(() => setCurrentStep(2), 300);
  };

  const handleNext = () => {
    // Validation for required steps
    if (currentStep === 1) {
      if (!formData.url) {
        alert('Please enter a website URL');
        return;
      }
      // Block analyzing our own website
      if (isBlockedUrl(formData.url)) {
        alert("😊 Thanks, but we already know our website is awesome! Please enter a different URL to analyze.");
        return;
      }
      if (!formData.websiteType) {
        alert('Please select a website type (Business or Personal)');
        return;
      }
    }
    if (currentStep === 2 && formData.objectives.length === 0) {
      alert('Please select at least one objective');
      return;
    }
    
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = () => {
    // Final validation - block our own website
    if (isBlockedUrl(formData.url)) {
      alert("😊 Thanks, but we already know our website is awesome! Please enter a different URL to analyze.");
      return;
    }
    onComplete(formData);
  };

  const isStepComplete = (step) => {
    switch(step) {
      case 1: return !!formData.url;
      case 2: return !!formData.websiteType;
      case 3: return formData.objectives.length > 0;
      default: return true;
    }
  };

  const renderProgressBar = () => {
    return (
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          {[...Array(totalSteps)].map((_, idx) => (
            <div key={idx} className="flex items-center flex-1">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                idx + 1 < currentStep ? 'bg-green-500 text-white' :
                idx + 1 === currentStep ? 'bg-blue-600 text-white' :
                'bg-slate-200 text-slate-500'
              }`}>
                {idx + 1 < currentStep ? <Check size={16} /> : idx + 1}
              </div>
              {idx < totalSteps - 1 && (
                <div className={`flex-1 h-1 mx-2 ${
                  idx + 1 < currentStep ? 'bg-green-500' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>
        <div className="text-center text-sm text-slate-600 mt-2">
          {lang === 'es' ? `Paso ${currentStep} de ${totalSteps}` : `Step ${currentStep} of ${totalSteps}`}
        </div>
      </div>
    );
  };

  const renderStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-slate-900 mb-2">{tx.analyzeTitle}</h2>
              <p className="text-slate-600">{tx.analyzeSub}</p>
            </div>
            
            <div>
              <Label htmlFor="url" className="text-lg font-semibold">{tx.urlLabel}</Label>
              <Input
                id="url"
                type="url"
                placeholder={lang === 'es' ? 'https://ejemplo.com' : 'https://example.com'}
                value={formData.url}
                onChange={(e) => setFormData({...formData, url: e.target.value})}
                className="mt-2 h-12 text-lg"
                autoFocus
              />
              <p className="text-sm text-slate-500 mt-2">{tx.urlHelp}</p>
            </div>
            
            <div>
              <Label className="text-lg font-semibold mb-3 block">{tx.typeLabel}</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card 
                  className={`cursor-pointer border-2 hover:shadow-lg transition-all ${
                    formData.websiteType === 'business' ? 'border-blue-600 bg-blue-50' : 'border-slate-200'
                  }`}
                  onClick={() => handleWebsiteTypeSelect('business')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                      <Briefcase className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{tx.business}</h3>
                    <p className="text-sm text-slate-600">{tx.businessDesc}</p>
                  </CardContent>
                </Card>
                <Card 
                  className={`cursor-pointer border-2 hover:shadow-lg transition-all ${
                    formData.websiteType === 'personal' ? 'border-blue-600 bg-blue-50' : 'border-slate-200'
                  }`}
                  onClick={() => handleWebsiteTypeSelect('personal')}
                >
                  <CardContent className="p-6 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-8 w-8 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{tx.personal}</h3>
                    <p className="text-sm text-slate-600">{tx.personalDesc}</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-slate-900 mb-2">{tx.goalsTitle}</h2>
              <p className="text-slate-600">{tx.goalsSub}</p>
            </div>
            
            <div>
              <Label className="text-lg font-semibold mb-3 block">{tx.objectivesLabel}</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-60 overflow-y-auto">
                {objectiveOptions.slice(0, 8).map(option => (
                  <div
                    key={option}
                    onClick={() => handleMultiSelect('objectives', option)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.objectives.includes(option) 
                        ? 'border-blue-600 bg-blue-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        formData.objectives.includes(option) ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                      }`}>
                        {formData.objectives.includes(option) && <Check size={12} className="text-white" />}
                      </div>
                      <span className="font-medium text-sm">{option}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold mb-3 block">
                {tx.challengesLabel} <span className="text-sm font-normal text-slate-500">{tx.challengesOpt}</span>
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {challengeOptions.slice(0, 6).map(challenge => (
                  <div
                    key={challenge}
                    onClick={() => handleMultiSelect('challenges', challenge)}
                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      formData.challenges.includes(challenge) 
                        ? 'border-red-500 bg-red-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        formData.challenges.includes(challenge) ? 'bg-red-500 border-red-500' : 'border-slate-300'
                      }`}>
                        {formData.challenges.includes(challenge) && <Check size={12} className="text-white" />}
                      </div>
                      <span className="font-medium text-xs">{challenge}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-slate-900 mb-2">{tx.detailsTitle}</h2>
              <p className="text-slate-600">{tx.detailsSub}</p>
            </div>
            
            <div className="space-y-5">
              {formData.websiteType === 'business' && (
                <div>
                  <Label htmlFor="siteType" className="font-semibold">
                    {tx.bizTypeLabel} <span className="text-slate-400 font-normal">{tx.bizTypeOpt}</span>
                  </Label>
                  <select
                    id="siteType"
                    value={formData.siteType}
                    onChange={(e) => setFormData({...formData, siteType: e.target.value})}
                    className="mt-2 w-full h-11 border-2 border-slate-200 rounded-lg px-4"
                  >
                    <option value="">{tx.selectType}</option>
                    {siteTypeOptions.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  
                  {/* Show input if "Other" is selected */}
                  {formData.siteType === 'Other' && (
                    <Input
                      placeholder={tx.specifyType}
                      value={formData.siteTypeOther || ''}
                      onChange={(e) => setFormData({...formData, siteTypeOther: e.target.value})}
                      className="mt-3"
                    />
                  )}
                </div>
              )}

              <div>
                <Label htmlFor="industry" className="font-semibold">
                  {tx.industryLabel} <span className="text-slate-400 font-normal">{tx.industryHint}</span>
                </Label>
                <Input
                  id="industry"
                  placeholder={tx.industryPh}
                  value={formData.industry}
                  onChange={(e) => setFormData({...formData, industry: e.target.value})}
                  className="mt-2"
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <Label className="font-semibold mb-3 block">{tx.mobileAppQ}</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[{ k: 'Yes', l: tx.mobileAppYes }, { k: 'No', l: tx.mobileAppNo }, { k: 'Planning To', l: tx.mobileAppPlan }].map(option => (
                    <div
                      key={option.k}
                      onClick={() => setFormData({...formData, hasMobileApp: option.k})}
                      className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-all text-sm ${
                        formData.hasMobileApp === option.k 
                          ? 'border-blue-600 bg-blue-100 font-semibold' 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      {option.l}
                    </div>
                  ))}
                </div>
                
                {formData.hasMobileApp === 'Yes' && (
                  <div className="mt-3">
                    <Label htmlFor="mobileAppUrl" className="font-semibold text-sm">
                      {tx.mobileAppLink} <span className="text-slate-400 font-normal">{tx.bizTypeOpt}</span>
                    </Label>
                    <Input
                      id="mobileAppUrl"
                      placeholder="e.g., https://apps.apple.com/... or https://play.google.com/..."
                      value={formData.mobileAppUrl || ''}
                      onChange={(e) => setFormData({...formData, mobileAppUrl: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                )}
              </div>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <Label className="font-semibold mb-3 block">{tx.ga4Q}</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[{ k: 'Yes', l: tx.ga4Yes }, { k: 'No', l: tx.ga4No }, { k: "Don't Know", l: tx.ga4Unknown }].map(option => (
                    <div
                      key={option.k}
                      onClick={() => setFormData({...formData, hasGA4: option.k})}
                      className={`p-3 border-2 rounded-lg cursor-pointer text-center transition-all text-sm ${
                        formData.hasGA4 === option.k 
                          ? 'border-green-600 bg-green-50 font-semibold' 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                    >
                      {option.l}
                    </div>
                  ))}
                </div>
              </div>

              {formData.hasGA4 === 'Yes' && (
                <div className="space-y-4 pl-4 border-l-4 border-blue-300">
                  <div>
                    <Label htmlFor="monthlyOrganicSessions" className="font-semibold">
                      {tx.organicSessions} <span className="text-slate-400 font-normal">{tx.leaveBlank}</span>
                    </Label>
                    <Input
                      id="monthlyOrganicSessions"
                      type="number"
                      placeholder="e.g., 5000"
                      value={formData.monthlyOrganicSessions}
                      onChange={(e) => setFormData({...formData, monthlyOrganicSessions: e.target.value})}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="bounceRate" className="font-semibold">
                      Current bounce rate <span className="text-slate-400 font-normal">(Leave blank if unknown)</span>
                    </Label>
                    <Input
                      id="bounceRate"
                      type="number"
                      placeholder="e.g., 45%"
                      value={formData.bounceRate}
                      onChange={(e) => setFormData({...formData, bounceRate: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                </div>
              )}

              {formData.websiteType === 'business' && (
                <>
                  {/* Car/RV Dealer Specific Questions */}
                  {(formData.siteType === 'Car Dealership' || formData.siteType === 'RV Dealership') && (
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-2 border-orange-200">
                      <h3 className="font-bold text-lg mb-3 text-orange-900">Dealership-Specific Questions</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <Label className="font-semibold mb-2 block">
                            Which inventory feed services do you use? <span className="text-slate-400 font-normal">(Select all that apply)</span>
                          </Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {['AutoTrader.com', 'Cars.com', 'CarGurus.com', 'Facebook Marketplace', 'Craigslist', 'Other'].map(service => (
                              <div
                                key={service}
                                onClick={() => handleMultiSelect('inventoryFeeds', service)}
                                className={`p-2 border-2 rounded-lg cursor-pointer text-center transition-all text-sm ${
                                  formData.inventoryFeeds?.includes(service) 
                                    ? 'border-orange-600 bg-orange-100 font-semibold' 
                                    : 'border-slate-200 hover:border-slate-300 bg-white'
                                }`}
                              >
                                {service}
                              </div>
                            ))}
                          </div>
                          {formData.inventoryFeeds?.includes('Other') && (
                            <Input
                              placeholder="Please specify other feed services"
                              value={formData.inventoryFeedsOther || ''}
                              onChange={(e) => setFormData({...formData, inventoryFeedsOther: e.target.value})}
                              className="mt-3"
                            />
                          )}
                        </div>

                        <div>
                          <Label className="font-semibold mb-2 block">
                            Do you have online service scheduling on your website?
                          </Label>
                          <div className="grid grid-cols-3 gap-2">
                            {['Yes', 'No', "Don't Know"].map(option => (
                              <div
                                key={option}
                                onClick={() => setFormData({...formData, hasServiceScheduling: option})}
                                className={`p-2 border-2 rounded-lg cursor-pointer text-center transition-all text-sm ${
                                  formData.hasServiceScheduling === option 
                                    ? 'border-green-600 bg-green-50 font-semibold' 
                                    : 'border-slate-200 hover:border-slate-300 bg-white'
                                }`}
                              >
                                {option}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <Label className="font-semibold mb-2 block">
                            Service department features on your website <span className="text-slate-400 font-normal">(Select all that apply)</span>
                          </Label>
                          <div className="grid grid-cols-2 gap-2">
                            {['Service Specials', 'Customer Portal', 'Live Chat', 'Service History', 'Mobile Payments', 'None'].map(feature => (
                              <div
                                key={feature}
                                onClick={() => handleMultiSelect('serviceDeptFeatures', feature)}
                                className={`p-2 border-2 rounded-lg cursor-pointer text-center transition-all text-xs ${
                                  formData.serviceDeptFeatures?.includes(feature) 
                                    ? 'border-blue-600 bg-blue-50 font-semibold' 
                                    : 'border-slate-200 hover:border-slate-300 bg-white'
                                }`}
                              >
                                {feature}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="pt-4 border-t border-orange-300">
                          <h4 className="font-bold mb-3 text-orange-900">Pricing Information</h4>
                          
                          <div className="space-y-3">
                            <div>
                              <Label className="font-semibold mb-2 block">
                                Inventory Size
                              </Label>
                              <select
                                value={formData.inventorySize || ''}
                                onChange={(e) => setFormData({...formData, inventorySize: e.target.value})}
                                className="w-full h-10 border-2 border-slate-200 rounded-lg px-3 text-sm"
                              >
                                <option value="">Select inventory size...</option>
                                <option value="0-25">0-25 vehicles</option>
                                <option value="26-50">26-50 vehicles</option>
                                <option value="51-75">51-75 vehicles</option>
                                <option value="76-100">76-100 vehicles</option>
                                <option value="100+">100+ vehicles</option>
                              </select>
                            </div>

                            <div>
                              <Label className="font-semibold mb-2 block">
                                Number of Locations
                              </Label>
                              <select
                                value={formData.numberOfLocations || ''}
                                onChange={(e) => setFormData({...formData, numberOfLocations: e.target.value})}
                                className="w-full h-10 border-2 border-slate-200 rounded-lg px-3 text-sm"
                              >
                                <option value="">Select...</option>
                                <option value="1">Single Location</option>
                                <option value="2">2 Locations</option>
                                <option value="3">3 Locations</option>
                                <option value="4">4 Locations</option>
                                <option value="5+">5+ Locations</option>
                              </select>
                              <p className="text-xs text-green-600 mt-1">Multi-location discounts available!</p>
                            </div>

                            <div>
                              <Label className="font-semibold mb-2 block">
                                Dealership Type
                              </Label>
                              <div className="grid grid-cols-2 gap-2">
                                {['New Car Franchise', 'Used Car/Independent'].map(type => (
                                  <div
                                    key={type}
                                    onClick={() => setFormData({...formData, dealershipType: type})}
                                    className={`p-2 border-2 rounded-lg cursor-pointer text-center transition-all text-xs ${
                                      formData.dealershipType === type 
                                        ? 'border-blue-600 bg-blue-50 font-semibold' 
                                        : 'border-slate-200 hover:border-slate-300 bg-white'
                                    }`}
                                  >
                                    {type}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="border-2 border-amber-200 bg-amber-50 p-4 rounded-lg">
                    <Label htmlFor="keyCompetitors" className="font-semibold flex items-center gap-2">
                      🔥 Key Competitors 
                      <Badge className="bg-amber-100 text-amber-700 text-xs">Highly Recommended</Badge>
                    </Label>
                    <p className="text-sm text-amber-700 mt-1 mb-2">
                      See how your website stacks up against the competition!
                    </p>
                    <Input
                      id="keyCompetitors"
                      placeholder="e.g., competitor1.com, competitor2.com"
                      value={formData.keyCompetitors}
                      onChange={(e) => setFormData({...formData, keyCompetitors: e.target.value})}
                      className="bg-white"
                    />
                    <p className="text-xs text-amber-600 mt-2">
                      ⚠️ Warning: You might not like what you discover about your competitors!
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="monthlyTrafficGoal" className="font-semibold">
                      Monthly Traffic Goal <span className="text-slate-400 font-normal">(Leave blank if unsure)</span>
                    </Label>
                    <Input
                      id="monthlyTrafficGoal"
                      placeholder="e.g., 10,000 visitors"
                      value={formData.monthlyTrafficGoal}
                      onChange={(e) => setFormData({...formData, monthlyTrafficGoal: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <p className="text-sm text-slate-900">
                <strong>Tip:</strong> The more details you provide, the more personalized your recommendations will be. But it's okay to skip fields you're unsure about!
              </p>
            </div>
          </div>
        );

      case 4:
      default:
        return renderReviewStep();

    }
  };

  const renderReviewStep = () => {
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">✓ Review & Submit</h2>
          <p className="text-slate-600">Review your information before analysis</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-50 rounded-lg p-6 space-y-4 max-h-96 overflow-y-auto">
          <div className="bg-white p-4 rounded-lg">
            <h3 className="font-bold text-sm text-slate-500 mb-1">Website URL</h3>
            <p className="text-lg">{formData.url}</p>
          </div>

          <div className="bg-white p-4 rounded-lg">
            <h3 className="font-bold text-sm text-slate-500 mb-1">Type</h3>
            <p className="text-lg capitalize">
              {formData.siteType 
                ? (formData.siteType === 'Other' && formData.siteTypeOther 
                    ? formData.siteTypeOther 
                    : formData.siteType)
                : formData.websiteType}
            </p>
          </div>

          {formData.objectives.length > 0 && (
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-bold text-sm text-slate-500 mb-2">Objectives</h3>
              <div className="flex flex-wrap gap-2">
                {formData.objectives.map((obj, idx) => (
                  <Badge key={idx} variant="secondary">{obj}</Badge>
                ))}
              </div>
            </div>
          )}

          {formData.industry && (
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-bold text-sm text-slate-500 mb-1">Industry</h3>
              <p className="text-lg">{formData.industry}</p>
            </div>
          )}

          {formData.hasGA4 && (
            <div className="bg-white p-4 rounded-lg">
              <h3 className="font-bold text-sm text-slate-500 mb-1">GA4 Status</h3>
              <p className="text-lg">{formData.hasGA4}</p>
            </div>
          )}
        </div>

        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mt-6">
          <h3 className="font-bold text-lg mb-2">Ready to Analyze!</h3>
          <p className="text-sm text-slate-700">
            We'll use this information to provide a comprehensive, personalized analysis with actionable recommendations.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className="relative max-w-6xl mx-auto">
      {/* Background Container with Images */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl">
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-slate-900/85 to-slate-900/90 z-10" />
        
        {/* Background Image Grid */}
        <div className="absolute inset-0 grid grid-cols-3 gap-1 opacity-40">
          <div className="relative overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=600&q=80" 
              alt="" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&q=80" 
              alt="" 
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1516387938699-a93567ec168e?w=600&q=80" 
              alt="" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>
        
        {/* Animated Gradient Orbs */}
        <div className="absolute top-10 left-10 w-72 h-72 bg-blue-500/30 rounded-full blur-3xl animate-pulse z-5" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse z-5" style={{animationDelay: '1s'}} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-cyan-500/20 rounded-full blur-3xl animate-pulse z-5" style={{animationDelay: '2s'}} />
      </div>
      
      {/* Main Content */}
      <div className="relative z-20 p-8 md:p-12">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
            <span className="text-green-400 text-sm font-semibold">{lang === 'es' ? '100% GRATIS' : '100% FREE'}</span>
            <span className="text-white/60 text-sm">•</span>
            <span className="text-white/80 text-sm">{lang === 'es' ? 'Sin Tarjeta de Crédito' : 'No Credit Card Required'}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
            {lang === 'es' ? 'Obtén tu Análisis Gratis del Sitio Web' : 'Get Your Free Website Analysis'}
          </h2>
          <p className="text-lg text-blue-100/80 max-w-2xl mx-auto">
            {lang === 'es' ? 'Descubre problemas ocultos que afectan tu tráfico, SEO y conversiones en menos de 60 segundos' : 'Discover hidden issues affecting your traffic, SEO, and conversions in under 60 seconds'}
          </p>
        </div>
        
        {/* Wizard Card */}
        <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border-0 p-8">
          {renderProgressBar()}
          {renderStep()}
          
          <div className="flex justify-between mt-8">
          <Button
            onClick={handleBack}
            disabled={currentStep === 1}
            variant="outline"
            className="flex items-center gap-2"
          >
            <ChevronLeft size={16} />
            {tx.back}
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
            >
              {tx.next}
              <ChevronRight size={16} />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-lg px-8 py-6"
            >
              <Check size={20} />
              {lang === 'es' ? 'Finalizar y Analizar' : 'Finish & Analyze'}
            </Button>
          )}
        </div>
        </Card>
        
        {/* Trust Badges Below Wizard */}
        <div className="flex flex-wrap justify-center items-center gap-6 mt-8 text-white/70 text-sm">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>{tx.sitesAnalyzed}</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span>{tx.starRated}</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>{tx.securePrivate}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyzerWizard;
